import { GoogleFont } from 'dragonmouse-shared/interfaces';

// All possible font weights (Google Fonts supports these discrete values)
export const ALL_WEIGHTS = ['100', '200', '300', '400', '500', '600', '700', '800', '900'];

// Get available weights for a font family from Google Fonts
export const getAvailableWeights = (fontFamily: string, googleFonts: GoogleFont[]): string[] => {
	const font = googleFonts.find(
		(f) => f.family.toLowerCase() === fontFamily.toLowerCase()
	);
	if (!font) return ['400', '500', '600', '700']; // Default fallback

	// Parse variants to extract weights (e.g., "regular" -> "400", "700" -> "700", "700italic" -> "700")
	const weights = new Set<string>();
	font.variants.forEach((variant) => {
		if (variant === 'regular' || variant === 'italic') {
			weights.add('400');
		} else {
			const weight = variant.replace('italic', '');
			if (weight && !isNaN(Number(weight))) {
				weights.add(weight);
			}
		}
	});

	// Return all weights this font supports, sorted
	return ALL_WEIGHTS.filter((w) => weights.has(w));
};

// Get fallback options based on the font's actual category
export const getFallbackOptions = (fontFamily: string, googleFonts: GoogleFont[]) => {
	const font = googleFonts.find(
		(f) => f.family.toLowerCase() === fontFamily.toLowerCase()
	);
	
	if (!font) {
		// Default fallback if font not found
		return [{ value: 'sans-serif', label: 'Sans-serif' }];
	}

	// Map Google Fonts category to CSS generic family
	// Google Fonts categories: "sans-serif", "serif", "display", "handwriting", "monospace"
	const categoryMap: Record<string, { value: string; label: string }[]> = {
		'sans-serif': [{ value: 'sans-serif', label: 'Sans-serif' }],
		serif: [{ value: 'serif', label: 'Serif' }],
		monospace: [{ value: 'monospace', label: 'Monospace' }],
		display: [
			{ value: 'sans-serif', label: 'Sans-serif' },
			{ value: 'serif', label: 'Serif' },
		],
		handwriting: [
			{ value: 'cursive', label: 'Cursive' },
			{ value: 'sans-serif', label: 'Sans-serif' },
		],
	};

	return categoryMap[font.category] || [{ value: 'sans-serif', label: 'Sans-serif' }];
};

// Infer font name from filename
// Handles patterns like:
// - Inter-Regular.woff2 -> Inter Regular
// - AtakBlack-Web.woff2 -> Atak Black
// - SourceSansPro-BoldItalic.woff2 -> Source Sans Pro Bold Italic
// - Inter-Regular-400.woff2 -> Inter Regular
// - latin-400-normal.woff2 -> Latin Normal
export const inferFontNameFromFilename = (filename: string): string => {
	// Remove extension
	let name = filename.replace(/\.(woff2?|ttf|otf|eot)$/i, '');
	
	// Remove common web font/distribution suffixes
	// Order matters - remove more specific patterns first
	name = name.replace(/[-_]latin[-_]ext$/i, ''); // latin-ext subset
	name = name.replace(/[-_]latin$/i, ''); // latin subset
	name = name.replace(/[-_]subset$/i, ''); // generic subset
	name = name.replace(/[-_]webfont$/i, ''); // webfont suffix
	name = name.replace(/[-_]web$/i, ''); // Web suffix
	name = name.replace(/[-_]woff$/i, ''); // Woff suffix
	name = name.replace(/[-_]v\d+$/i, ''); // version numbers (v1, v2, etc.)
	name = name.replace(/[-_]wght$/i, ''); // variable font weight axis
	
	// Remove numeric weights at the end (e.g., "-400", "-700", "400-normal")
	name = name.replace(/[-_](\d{3})[-_]?normal$/i, ''); // numeric-weight-normal pattern
	name = name.replace(/[-_](\d{3})$/i, ''); // numeric weight suffix
	
	// Remove "normal" style indicator if it's standalone
	name = name.replace(/[-_]normal$/i, '');
	
	// Handle camelCase font names (e.g., "AtakBlack" -> "Atak Black", "SourceSansPro" -> "Source Sans Pro")
	// Insert space before capital letters that follow lowercase letters or numbers
	name = name.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
	
	// Clean up separators (convert dashes/underscores to spaces)
	name = name.replace(/[-_]/g, ' ');
	
	// Capitalize words properly
	name = name.split(' ')
		.filter(word => word.length > 0) // Remove empty strings from multiple spaces
		.map(word => {
			// Preserve common weight/style words as-is (they're already capitalized)
			const preserved = ['Regular', 'Bold', 'Italic', 'Light', 'Medium', 'SemiBold', 'Semibold', 'ExtraBold', 'ExtraLight', 'Black', 'Thin', 'Normal', 'Oblique', 'Roman'];
			const lowerWord = word.toLowerCase();
			const preservedMatch = preserved.find(p => p.toLowerCase() === lowerWord);
			if (preservedMatch) return preservedMatch;
			
			// Capitalize first letter, lowercase rest
			return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
		})
		.join(' ');
	
	return name.trim() || 'Custom Font';
};

// Infer format from file extension
export const getFormatFromFile = (file: File): 'woff2' | 'woff' | null => {
	if (file.name.toLowerCase().endsWith('.woff2')) return 'woff2';
	if (file.name.toLowerCase().endsWith('.woff')) return 'woff';
	return null;
};

// Valid font file extensions and MIME types
export const VALID_FONT_EXTENSIONS = ['.woff', '.woff2'];
export const VALID_FONT_MIME_TYPES = [
	'font/woff',
	'font/woff2',
	'application/font-woff',
	'application/font-woff2',
];

// Validate a font file
export const isValidFontFile = (file: File): boolean => {
	const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
	const isValidExtension = VALID_FONT_EXTENSIONS.includes(fileExtension);
	const isValidMimeType = VALID_FONT_MIME_TYPES.includes(file.type);
	return isValidExtension || isValidMimeType;
};

